import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { IConnectionDetailsDTO } from '../../../../api/connections.dao';
import ArrowUpSVG from '../../../../images/svg/icons8-arrow-up';
import ChevronSVG from '../../../../images/svg/icons8-expand-arrow';
import { getConnectionItemLabelText, isConnectionGroupType } from '../../../../infrastructure/utils/connections.utils';
import Text from '../../../global/text/text';
import { ConnectionDeleteDetails } from '../shared/connections-list/_connection-delete-message';
import { ConnectionDetailsPopover } from '../shared/connections-list/_connection-details-popover';
import { WebinarReportsViewModel } from '../shared/webinar-report/webinar-reports.viewmodel';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../tracking-service/tracking-values';
import { MeetingApiParams } from '../shared/connections-list/_connection-form/connection-form.viewmodel';

interface IProps {
    connection: IConnectionDetailsDTO;
    isDefault?: boolean;
    eventTimezone: string;
    orgConnection?: IConnectionDetailsDTO;
    cloneConnection: (connection: IConnectionDetailsDTO) => void;
    getInheritedConnection?: () => IConnectionDetailsDTO | undefined;
    handleOnChange: (connection: IConnectionDetailsDTO) => void;
    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void;
    setDeletePopoverConnection: (connection: IConnectionDetailsDTO | null) => void;
    webinarReportsVM?: WebinarReportsViewModel;
    isOrg?: boolean;
    isRoom?: boolean;
    knovioReplayConnection?: IConnectionDetailsDTO;
    handleTrackingEvent?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => void;
    handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    hideChevron?: boolean;
    meetingParams: MeetingApiParams;
    onOpenKnovioPreviewLinkPopover: (connection: IConnectionDetailsDTO) => void;
    shouldDisableDeleteButton: boolean;
}

const ConnectionListItem = (props: IProps) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
    const {
        connection,
        getInheritedConnection,
        setDeletePopoverConnection,
        handleOnChange,
        showConfigurePassportForm,
        orgConnection,
        cloneConnection,
        webinarReportsVM,
        isOrg,
        isRoom = false,
        knovioReplayConnection,
        handleTrackingEvent,
        handleTroubleshoot,
        trackHostJoinClick,
        hideChevron,
        meetingParams,
        onOpenKnovioPreviewLinkPopover,
        shouldDisableDeleteButton,
    } = props;

    const listItemRef = useRef(null);
    const deleteItemRef = useRef(null);
    const isCancelledConnection = connection?.cancelled ?? false;

    if (knovioReplayConnection) {
        return (
            <div className='connection-list-item meeting-list-item meeting-list-item__text'>
                    <div className='connection-list-item__inherited'>
                        <ArrowUpSVG />
                        <Text
                            className={classNames('inherited-connection', { 'inherited-connection--delete': knovioReplayConnection.cancelled })}
                            message={getConnectionItemLabelText(knovioReplayConnection)}
                            withEllipsis
                        />
                    </div>
            </div>
        );
    }

    if (connection) {
        return (
            <>
                <div className={classNames('connection-list-item meeting-list-item meeting-list-item__text connection-list-block', {
                    'connection-list__item--active': isPopoverOpen
                })}>
                   <div ref={deleteItemRef}>
                        <ConnectionDeleteDetails
                            eventTimezone={props.eventTimezone}
                            isCancelled={isCancelledConnection}
                            cancelDateTime={connection?.cancelDateTime}
                            listItemRef={deleteItemRef}
                        />
                    </div>
                    <div ref={listItemRef}
                        onClick={e => {
                            e.stopPropagation();

                            if (isConnectionGroupType(connection)) return;

                            setIsPopoverOpen(!isPopoverOpen);
                            // Track PopupOpen Event to Mixpanel
                            if (handleTrackingEvent) {
                                handleTrackingEvent(CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CALL);
                            }
                        }}
                        className={isRoom ? 'connection-list__item' : 'connection-list-item__meeting-content'}>
                        <span className={classNames('connection-list-item__description w-100',
                            { 'connection-list-item__description--delete': isCancelledConnection })}>
                            <Text
                                message={getConnectionItemLabelText(connection)}
                                withEllipsis
                            />
                            {props.isDefault && <span className='connection-list-item__default' />}
                        </span>
                        {!hideChevron && <span className='connection-list-item__chevron'>
                            <ChevronSVG />
                        </span>}
                    </div>
                    <ConnectionDetailsPopover
                        connection={connection}
                        handleOnChange={handleOnChange}
                        showConfigurePassportForm={showConfigurePassportForm}
                        cloneConnection={cloneConnection}
                        setDeletePopoverConnection={setDeletePopoverConnection}
                        isPopoverOpen={isPopoverOpen}
                        setIsPopoverOpen={setIsPopoverOpen}
                        listItemRef={listItemRef}
                        itemWidth={198}
                        webinarReportsVM={webinarReportsVM}
                        handleTrackingEvent={handleTrackingEvent}
                        handleTroubleshoot={handleTroubleshoot}
                        trackHostJoinClick={trackHostJoinClick}
                        meetingParams={meetingParams}
                        onOpenKnovioPreviewLinkPopover={() => onOpenKnovioPreviewLinkPopover(connection)}
                        shouldDisableDeleteButton={shouldDisableDeleteButton}
                    />
                </div>
            </>
        );
    }

    const inheritedConnection = orgConnection || (getInheritedConnection ? getInheritedConnection() : undefined);
    const isCancelledInheritedConnection = inheritedConnection?.cancelled ?? false;

    return (
        <div className='connection-list-item meeting-list-item meeting-list-item__text'>
            {inheritedConnection &&
                <div className='connection-list-item__inherited'>
                    <ArrowUpSVG />
                    <Text
                        className={classNames('inherited-connection', { 'inherited-connection--delete': isCancelledInheritedConnection })}
                        message={getConnectionItemLabelText(inheritedConnection)}
                        withEllipsis
                    />
                </div>}
            {!inheritedConnection && !isOrg && <Text className='no-connection' message='Connection required' />}
        </div>);
};

export { ConnectionListItem };

